/* istanbul ignore file */
import React, { FC, useEffect, useRef, useState } from 'react'
import {
  Root as RadixDropdownMenu,
  Trigger,
  Content,
  Portal,
} from '@radix-ui/react-dropdown-menu'
import { Box, ScreenReaderOnly } from '@moonpig/launchpad-components'
import { IconSystemSort } from '@moonpig/launchpad-assets'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { useIsMobile } from '@moonpig/web-shared-utils'
import { useDrawer } from '../../../contexts/drawer'
import { useFindLocaleText } from '../../../text-localisation'
import { SortOptions } from '../../FiltersMenu/components/SortBy/SortOptions'
import { ResultsButton } from '../../FiltersMenu/components'

const MENU_OFFSET_PX = '144px'
const MENU_MAX_HEIGHT_PX = '480px'
const MENU_MIN_WIDTH_PX = '240px'
const ICON_SIZE = 24
const QUICK_SORT_BY_DROPDOWN_ID = 'quick-sort-by-dropdown-button'

const StyledDropdownContent = styled(Content)`
  position: relative;
  overflow-y: auto;
  overscroll-behavior: contain;
  max-height: min(calc(100vh - ${MENU_OFFSET_PX}), ${MENU_MAX_HEIGHT_PX});
  z-index: 1050;
  ${s({
    bgcolor: 'colorBackground01',
    minWidth: MENU_MIN_WIDTH_PX,
    boxShadow: 4,
    borderRadius: 4,
    borderColor: 'colorBorder03',
    border: 2,
  })};
`

const StyledSortButton = styled.button`
  transition: background-color 100ms ease-in-out, border-color 100ms ease-in-out,
    color 100ms ease-in-out;

  ${s({
    py: 0,
    my: { xs: 4, md: 5 },
    mr: { xs: 7, lg: 0 },
    ml: 3,
    p: 5,
    borderColor: 'colorBlack20',
    bgcolor: 'colorBackground01',
    border: 2,
    color: 'colorTextLabel',
    boxShadow: 0,
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    typography: 'typeButtonLabel',
    borderRadius: 2,
    height: '48px',
    gap: 4,
  })};

  &:active {
    outline: none;
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.2);
  }

  &:not(:disabled) {
    &:hover {
      ${s({
        boxShadow: 0,
        borderColor: 'colorBlack30',
        bgcolor: 'colorBlack05',
        color: 'colorTextLabel',
      })}
    }

    &:focus,
    &[data-state='open'],
    &[aria-expanded='true'] {
      outline: none;
      ${s({
        borderColor: 'colorBlack30',
        bgcolor: 'colorBlack05',
        color: 'colorTextLabel',
      })}
    }

    &:focus:not(:focus-visible) {
      box-shadow: none;
    }
  }
`

export type Props = {
  sortOptions: { label: string; value: string }[]
  sortValue: string
  onSortBy: (sortValue: string) => void
  loading: boolean
}

export const QuickSortByDropDown: FC<Props> = ({
  sortOptions,
  sortValue,
  onSortBy,
  loading,
}) => {
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useIsMobile()
  const {
    show: showDrawer,
    close: closeDrawer,
    update: updateDrawer,
  } = useDrawer()
  const localiseText = useFindLocaleText()

  const sortLabel = localiseText('find.sort')
  const sortByTitle = localiseText('find.sort_by')

  const handleSortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSortBy(e.target.value)
  }

  const handleViewResults = () => {
    setIsOpen(false)
    closeDrawer()
  }

  const mobileSortOptions = (
    <SortOptions
      options={sortOptions}
      value={sortValue}
      onChange={handleSortChange}
      disabled={false}
      ariaLabelledBy={QUICK_SORT_BY_DROPDOWN_ID}
    />
  )

  const desktopSortOptions = (
    <SortOptions
      options={sortOptions}
      value={sortValue}
      onChange={handleSortChange}
      disabled={false}
      ariaLabelledBy={QUICK_SORT_BY_DROPDOWN_ID}
      inDropdownMenu
    />
  )

  const mobileSortContent = (
    <>
      {mobileSortOptions}
      <Box borderTop={1} borderColor="colorBorder03">
        <ResultsButton
          onClick={handleViewResults}
          loading={loading}
          resultsCount={0}
          label={localiseText('find.view_results')}
          compact
        />
      </Box>
    </>
  )

  useEffect(() => {
    if (isMobile && isOpen) {
      updateDrawer([mobileSortContent], undefined, undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isOpen, sortValue, sortOptions, loading])

  return isMobile ? (
    <>
      <ScreenReaderOnly id={QUICK_SORT_BY_DROPDOWN_ID}>
        {sortLabel}
      </ScreenReaderOnly>
      <StyledSortButton
        ref={dropdownButtonRef}
        onClick={() => {
          setIsOpen(true)
          showDrawer({
            title: sortByTitle,
            triggerRef: undefined,
            header: undefined,
            footer: undefined,
            body: [mobileSortContent],
            onClose: () => setIsOpen(false),
          })
        }}
      >
        {sortLabel}
        <IconSystemSort aria-hidden width={ICON_SIZE} height={ICON_SIZE} />
      </StyledSortButton>
    </>
  ) : (
    <>
      <ScreenReaderOnly id={QUICK_SORT_BY_DROPDOWN_ID}>
        {sortLabel}
      </ScreenReaderOnly>
      <RadixDropdownMenu
        onOpenChange={open => setIsOpen(open)}
        open={isOpen}
        modal={false}
      >
        <Trigger asChild>
          <StyledSortButton>
            {sortLabel}
            <IconSystemSort aria-hidden width={ICON_SIZE} height={ICON_SIZE} />
          </StyledSortButton>
        </Trigger>
        <Portal>
          <StyledDropdownContent
            sideOffset={8}
            align="end"
            side="bottom"
            avoidCollisions={false}
          >
            {desktopSortOptions}
          </StyledDropdownContent>
        </Portal>
      </RadixDropdownMenu>
    </>
  )
}
